import { BULK_CHANGE_KINDS, BULK_CHANGE_MAX_BYTES } from '@btsis/core';
import { oc } from '@orpc/contract';
import { z } from 'zod';
import { commonErrors } from './common/errors.js';
import { listSearchSchema } from './common/list.js';
import { paginated, paginationInputSchema } from './common/pagination.js';
import { queryBooleanSchema } from './common/query.js';
import { fileNameSchema, presignedUploadSchema, uploadTokenSchema } from './common/upload.js';
import { donationExportInputSchema } from './donation.js';
import { generatedFileSchema } from './generated-file.js';
import { ATTACHMENT_SCAN_STATUSES } from './statement.js';

const guarded = {
  UNAUTHENTICATED: commonErrors.UNAUTHENTICATED,
  PERMISSION_DENIED: commonErrors.PERMISSION_DENIED,
  TWO_FACTOR_REQUIRED: commonErrors.TWO_FACTOR_REQUIRED,
  OUT_OF_SCOPE: commonErrors.OUT_OF_SCOPE,
  NOT_FOUND: commonErrors.NOT_FOUND,
  CONFLICT: commonErrors.CONFLICT,
  VALIDATION_FAILED: commonErrors.VALIDATION_FAILED,
};

export const bulkChangeKindSchema = z.enum(BULK_CHANGE_KINDS);
export type BulkChangeKindName = z.infer<typeof bulkChangeKindSchema>;

/** A bulk change's life — docs/decisions/0125 §3–§5, the donation import's own. */
export const BULK_CHANGE_STATUSES = [
  'uploaded',
  'processing',
  'preview',
  'committing',
  'committed',
  'failed',
  'discarded',
] as const;
export const bulkChangeStatusSchema = z.enum(BULK_CHANGE_STATUSES);
export type BulkChangeStatus = z.infer<typeof bulkChangeStatusSchema>;

/**
 * What the preview judged a row to be, and what the commit then did with it:
 * a record to create, a record to update, one already as the file says, or a
 * row the single-record path would refuse.
 */
export const BULK_CHANGE_ROW_ACTIONS = ['create', 'update', 'unchanged', 'rejected'] as const;
export type BulkChangeRowAction = (typeof BULK_CHANGE_ROW_ACTIONS)[number];
export const BULK_CHANGE_ROW_RESULTS = ['planned', 'applied', 'refused'] as const;
export type BulkChangeRowResult = (typeof BULK_CHANGE_ROW_RESULTS)[number];

/**
 * What a commit leaves on an applied row besides its target: whether the
 * invitation of a created user left by mail. A stable code the screen words,
 * never the link itself — docs/decisions/0105.
 */
export const BULK_CHANGE_RESULT_NOTES = ['invitation_sent', 'invitation_not_sent'] as const;
export const bulkChangeResultNoteSchema = z.enum(BULK_CHANGE_RESULT_NOTES);
export type BulkChangeResultNote = z.infer<typeof bulkChangeResultNoteSchema>;
export const BULK_CHANGE_ROW_VIEWS = ['all', ...BULK_CHANGE_ROW_ACTIONS] as const;
export type BulkChangeRowView = (typeof BULK_CHANGE_ROW_VIEWS)[number];

export const BULK_CHANGE_SORT_FIELDS = ['createdAt', 'rowCount'] as const;

/**
 * Why a row was refused: the stable code the single-record path threw — a
 * `DomainError`'s code with its `details.reason` — the column it is about, when
 * one is, and the scalar details the screen interpolates into `error.<code>`.
 */
export const bulkChangeIssueSchema = z.object({
  code: z.string(),
  reason: z.string().nullable(),
  column: z.string().nullable(),
  details: z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()])),
});
export type BulkChangeIssue = z.infer<typeof bulkChangeIssueSchema>;

/** One field of one row the file changes: the column key, what is stored, what the file says. */
export const bulkChangeFieldChangeSchema = z.object({
  field: z.string(),
  from: z.string().nullable(),
  to: z.string().nullable(),
});
export type BulkChangeFieldChange = z.infer<typeof bulkChangeFieldChangeSchema>;

export const bulkChangeColumnSchema = z.object({
  key: z.string(),
  label: z.string(),
  editable: z.boolean(),
});
export type BulkChangeColumn = z.infer<typeof bulkChangeColumnSchema>;

export const bulkChangeSchema = z.object({
  id: z.uuid(),
  kind: bulkChangeKindSchema,
  status: bulkChangeStatusSchema,
  fileName: z.string(),
  fileSize: z.string(),
  scanStatus: z.enum(ATTACHMENT_SCAN_STATUSES),
  rowCount: z.number().int(),
  createCount: z.number().int(),
  updateCount: z.number().int(),
  unchangedCount: z.number().int(),
  rejectedCount: z.number().int(),
  appliedCount: z.number().int(),
  refusedCount: z.number().int(),
  failureCode: z.string().nullable(),
  /** The campaign a donation change was laid out for; null for every other kind. */
  campaignId: z.uuid().nullable(),
  campaignName: z.string().nullable(),
  uploadedByUserId: z.uuid(),
  uploadedByName: z.string(),
  commitRequestedAt: z.string().nullable(),
  committedAt: z.string().nullable(),
  failedAt: z.string().nullable(),
  discardedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type BulkChange = z.infer<typeof bulkChangeSchema>;

/** One change with the template's columns by key and label, which the preview heads its rows with. */
export const bulkChangeDetailSchema = bulkChangeSchema.extend({
  columns: z.array(bulkChangeColumnSchema),
});
export type BulkChangeDetail = z.infer<typeof bulkChangeDetailSchema>;

export const bulkChangeRowSchema = z.object({
  id: z.uuid(),
  rowNumber: z.number().int(),
  action: z.enum(BULK_CHANGE_ROW_ACTIONS),
  result: z.enum(BULK_CHANGE_ROW_RESULTS),
  targetId: z.uuid().nullable(),
  targetLabel: z.string().nullable(),
  changes: z.array(bulkChangeFieldChangeSchema),
  issues: z.array(bulkChangeIssueSchema),
  resultNote: bulkChangeResultNoteSchema.nullable(),
  /** The row as the file had it, by column key; null once the change has ended. */
  cells: z.record(z.string(), z.string()).nullable(),
});
export type BulkChangeRow = z.infer<typeof bulkChangeRowSchema>;

/**
 * The filter a template is drawn from: the list's own, without its page. A
 * template with no filter is empty — the header and the stamp, ready to fill —
 * and one with a filter holds every matching row the caller's scope reaches.
 */
export const userBulkFilterSchema = z.object({
  search: listSearchSchema,
  institutionId: z.uuid().optional(),
  roleId: z.uuid().optional(),
  status: z.enum(['all', 'active', 'inactive']).default('all'),
});

export const institutionBulkFilterSchema = z.object({
  search: z.string().trim().optional(),
  countryId: z.uuid().optional(),
  zoneId: z.uuid().optional(),
  regionId: z.uuid().optional(),
  organizationId: z.uuid().optional(),
  includeInactive: queryBooleanSchema.default(false),
});

export const bulkChangeTemplateInputSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('user'), filter: userBulkFilterSchema.optional() }),
  z.object({ kind: z.literal('institution'), filter: institutionBulkFilterSchema.optional() }),
  z.object({
    kind: z.literal('donation'),
    campaignId: z.uuid(),
    filter: donationExportInputSchema.optional(),
  }),
]);
export type BulkChangeTemplateInput = z.infer<typeof bulkChangeTemplateInputSchema>;

export const requestBulkChangeUploadInputSchema = z.object({
  kind: bulkChangeKindSchema,
  fileName: fileNameSchema,
  fileSize: z.number().int().min(1).max(BULK_CHANGE_MAX_BYTES),
});

export const createBulkChangeInputSchema = z.object({
  kind: bulkChangeKindSchema,
  uploadToken: uploadTokenSchema,
});
export type CreateBulkChangeInput = z.infer<typeof createBulkChangeInputSchema>;

export const bulkChangeListInputSchema = paginationInputSchema.extend({
  kind: bulkChangeKindSchema.optional(),
  status: bulkChangeStatusSchema.optional(),
});
export type BulkChangeListInput = z.infer<typeof bulkChangeListInputSchema>;

/**
 * The commit of a previewed change. It names the `updatedAt` the screen showed,
 * so a commit is never made of a preview that has moved since.
 */
export const commitBulkChangeInputSchema = z.object({
  id: z.uuid(),
  expectedUpdatedAt: z.iso.datetime({ offset: true }),
});
export type CommitBulkChangeInput = z.infer<typeof commitBulkChangeInputSchema>;

/**
 * Bulk add and update by template — docs/decisions/0125. Every route is behind
 * any one of the permissions that start a change; the service asks the kind's
 * own: users `user.list` with `user.create` or `user.edit`, institutions
 * `orgUnit.list` with `orgUnit.create` or `orgUnit.edit`, donations
 * `donation.list`, `donation.export` and `donation.edit`.
 */
export const bulkChangeContract = {
  /**
   * Asks the worker for a template — empty, or holding the rows of the list's
   * current filter within the caller's scope. The answer is the
   * `generated_files` row, which the screen then watches and downloads.
   */
  requestTemplate: oc
    .route({ method: 'POST', path: '/bulk-changes/templates' })
    .errors(guarded)
    .input(bulkChangeTemplateInputSchema)
    .output(generatedFileSchema),

  requestUpload: oc
    .route({ method: 'POST', path: '/bulk-changes/uploads' })
    .errors(guarded)
    .input(requestBulkChangeUploadInputSchema)
    .output(presignedUploadSchema),

  /**
   * The intent row. The server reads the uploaded bytes, admits an `.xlsx`,
   * `.xls` or `.csv`, hashes them and moves them to a key only it has written,
   * then commits the change `uploaded` and queues the scan and the preview.
   * Nothing is created or updated here.
   */
  create: oc
    .route({ method: 'POST', path: '/bulk-changes' })
    .errors(guarded)
    .input(createBulkChangeInputSchema)
    .output(bulkChangeDetailSchema),

  list: oc
    .route({ method: 'GET', path: '/bulk-changes' })
    .errors(guarded)
    .input(bulkChangeListInputSchema)
    .output(paginated(bulkChangeSchema)),

  get: oc
    .route({ method: 'GET', path: '/bulk-changes/{id}' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(bulkChangeDetailSchema),

  rows: oc
    .route({ method: 'GET', path: '/bulk-changes/{id}/rows' })
    .errors(guarded)
    .input(
      paginationInputSchema.extend({
        id: z.uuid(),
        view: z.enum(BULK_CHANGE_ROW_VIEWS).default('all'),
      }),
    )
    .output(paginated(bulkChangeRowSchema)),

  /**
   * The uploader's alone, and only of a preview: `preview → committing`, and
   * the job then applies each create and update through the single-record
   * path, one transaction per row. A second commit of the same preview is a
   * conflict and applies nothing.
   */
  commit: oc
    .route({ method: 'POST', path: '/bulk-changes/{id}/commit' })
    .errors(guarded)
    .input(commitBulkChangeInputSchema)
    .output(bulkChangeDetailSchema),

  /** The uploader's alone, and only before anything was applied from it. */
  discard: oc
    .route({ method: 'POST', path: '/bulk-changes/{id}/discard' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(bulkChangeDetailSchema),
};
